import { add as addDuration, formatISO } from 'date-fns';
import { bucketConfig } from 'app/agriculture/queries/snapshots/config/bucketConfig';
import { createAxiosInstance } from 'app/agriculture/queries/createAxiosInstance';
import { TZDate } from '@date-fns/tz';
import { SnapshotBucketQueryKey } from 'app/agriculture/queries/snapshots/types/SnapshotBucketQueryKey';
import {
    snapshotConfig,
    SnapshotInterface,
    SnapshotType,
} from 'app/agriculture/queries/snapshots/config/snapshotConfig';
import { handleZodParseError } from 'app/agriculture/utils';
import { groupByKey } from 'app/agriculture/queries/snapshots/utils/groupByKey';
import { SnapshotsMap } from 'app/agriculture/queries/snapshots/types/SnapshotsMap';
import { Snapshot } from 'app/agriculture/queries/snapshots/types/Snapshot';

/**
 * gets a snapshot bucket, parsed and grouped by parent id
 */
export async function getSnapshotBucket<
    T extends SnapshotType,
    I extends Snapshot = SnapshotInterface<T>,
>({
    queryKey: [snapshotType, { bucketTimestamp, farmId }],
}: {
    queryKey: SnapshotBucketQueryKey<T>;
}): Promise<SnapshotsMap<I>> {
    const instance = createAxiosInstance();
    const { bucketSize, endpoint, parseSnapshots } = snapshotConfig[snapshotType];
    const { bucketDuration } = bucketConfig[bucketSize];
    const start = new TZDate(bucketTimestamp, 'UTC');
    const end = addDuration(start, bucketDuration);

    const { data } = await instance.get<unknown[]>(endpoint, {
        params: {
            farm_id: farmId,
            timestamp__gte: formatISO(start),
            timestamp__lte: formatISO(end),
        },
    });

    try {
        /** parse the snapshots */
        const parsedSnapshots = parseSnapshots(data) as I[];

        /** group snapshots by parentId */
        return groupByKey<I>(parsedSnapshots, 'parentId');
    } catch (e) {
        handleZodParseError(e);
        throw e;
    }
}
